import styled from 'styled-components';

import { Theme, useTheme } from '@/services/theme';
import { breakpoints } from '@/shared/styles';
import { useBreakpointDown } from '@/shared/utils/useBreakpoint';
import { CookieBanner } from '@/features/cookies/CookieBanner';
import { NewFooter } from '@/shared/components/Footer/NewFooter';
import { ContactUs } from '@/pages/LandingPage/sections/ContactUs/ContactUs';
import { EarnBannerSection } from '@/pages/LandingPage/sections/EarnBannerSection/EarnBannerSection';

import { Features } from './sections/Features';
import { Header } from './components/Header';
import { Hero } from './sections/Hero';
import { Security } from './sections/Security';
import { Articles } from './sections/Articles';
import { PartnersSlider } from './sections/PartnersSlider/PartnersSlider';
import { Vaults } from './sections/Vaults';
import { Model } from './sections/Model';
import { SupportedTokens } from './sections/SupportedTokens';
import { Partners } from './sections/Partners';
import { Metrics } from './sections/Metrics/Metrics';
import BgBlur from './bg_blur.svg';

const Background = styled.div`
  width: 100%;
  min-height: 100vh;
  min-width: 350px;
  color: ${({ theme }: { theme: Theme }) => theme.colors.text.primary};
  background: ${({ theme }: { theme: Theme }) => theme.colors.background.dark};
  font-family: Helvetica, sans-serif;
  position: relative;

  .bg__ellipse {
    position: absolute;
    border-radius: 50%;
    z-index: 0;

    &_blue {
      width: 210px;
      height: 211px;
      top: 86px;
      left: calc((100vw - 1200px) / 2 - 133px);

      background: radial-gradient(
        50% 50% at 50% 50%,
        #131d52 25.29%,
        #2750aa 60.8%,
        #a3eaff 92.71%,
        #ffffff 100%
      );
      backdrop-filter: blur(25px);
      z-index: 1;

      @media (min-width: ${breakpoints.tabletSM}px) {
        width: 500px;
        height: 500px;
        top: 172px;
        left: calc((100vw - 1200px) / 2 - 247px);
      }
    }

    &_red {
      width: 324px;
      height: 325px;
      top: -118px;
      left: -128px;

      background: radial-gradient(50% 50% at 50% 50%, #ea0042 0%, #ec0b38 100%);
      box-shadow: 0px 0px 70px 0px rgba(236, 11, 56, 0.3);
      box-shadow: 0px 0px 100px 0px rgba(255, 255, 255, 1) inset;
      z-index: 0;

      @media (min-width: ${breakpoints.tabletSM}px) {
        width: 767px;
        height: 769px;
        top: -332px;
        left: calc((100vw - 1200px) / 2 - 256px);
      }
    }

    &_red_c1 {
      width: 212px;
      height: 213px;
      top: -62px;
      left: -72px;

      border: 1px solid rgba(255, 255, 255, 0.4);
      z-index: 0;

      @media (min-width: ${breakpoints.tabletSM}px) {
        width: 502px;
        height: 504px;
        top: -201px;
        left: calc((100vw - 1200px) / 2 - 123px);
      }
    }

    &_red_c2 {
      width: 447px;
      height: 449px;
      top: -180px;
      left: -190px;

      border: 1px solid rgba(47, 50, 63, 1);
      z-index: 0;

      @media (min-width: ${breakpoints.tabletSM}px) {
        width: 1060px;
        height: 1061px;
        top: -478px;
        left: calc((100vw - 1200px) / 2 - 402px);
      }
    }
  }

  .bg__blur_red {
    position: absolute;
    width: 324px;
    height: 325px;
    top: 0px;
    overflow: hidden;
    right: 0px;

    background-image: url(${BgBlur});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    overflow: hidden;
    z-index: 100;

    @media (min-width: ${breakpoints.tabletSM}px) {
      width: 687px;
      height: 610px;
      top: -78px;
      right: 0px;
    }
  }
`;

const Content = styled.main`
  width: 100%;
  max-width: 1200px;
  margin: auto;
  /* padding: 0 20px; */

  display: flex;
  flex-direction: column;
  font-family: Gramatika;

  @media (min-width: 1240px) {
    padding: 0;
  }

  & h2 {
    font-size: 32px;
    line-height: 50px;
    font-weight: 400;
    color: #fff;
    z-index: 1;

    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 60px;
      line-height: 130%;
    }
  }

  & h3 {
    font-size: 24px;
    line-height: 27px;
    font-weight: 700;
    color: #fff;

    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 32px;
      line-height: 47px;
    }
  }

  & h4 {
    font-size: 24px;
    line-height: 41.6px;
    font-weight: 400;
    color: #fff;

    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 32px;
    }
  }

  .Content__description1 {
    color: #b4b7cc;
    font-size: 20px;
    font-weight: 400;
    line-height: 32px;

    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 30px;
      line-height: 42px;
    }
  }

  .Content__description2 {
    color: #b4b7cc;
    font-size: 16px;
    font-weight: 400;
    line-height: 28px;

    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .Content__description3 {
    color: #b4b7cc;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.8px;

    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 16px;
    }
  }
`;

const HeroContainer = styled.div`
  width: 100%;
  padding-bottom: 4px;
  @media (min-width: ${breakpoints.tabletLG}px) {
    padding-bottom: 0px;
  }

  & h1 {
    font-weight: 400;
    line-height: 140%;
    font-family: Gramatika;
    text-align: left;

    font-size: 36px;
    @media (min-width: ${breakpoints.tabletSM}px) {
      font-size: 72px;
    }
  }
`;

export const LandingPage = () => {
  const { theme } = useTheme();

  const isMobile = useBreakpointDown(425);

  return (
    <Background theme={theme}>
      <div className="bg__ellipse bg__ellipse_blue" />
      <div className="bg__ellipse bg__ellipse_red" />
      <div className="bg__ellipse bg__ellipse_red_c1" />
      <div className="bg__ellipse bg__ellipse_red_c2" />
      <div className="bg__blur_red" />

      <Header color="transparent" scrollYThreshold={isMobile ? 106 : 180} />
      <HeroContainer>
        <Hero />
      </HeroContainer>
      <Content>
        <PartnersSlider />
        <EarnBannerSection />
        <Features />
        <Metrics />
        <Model />
        <Vaults />
        <SupportedTokens />
        <Security />
        <Articles />
        <Partners />
      </Content>
      <ContactUs />
      <NewFooter showV1Link />
      <CookieBanner />
    </Background>
  );
};
